/* eslint-disable react/display-name */
import { useEffect, useState } from 'react';

import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { HookFormResolver } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { File } from 'app/components/form';
import { message } from 'app/modules/Taxpayer/TaxpayerRequest/translation';
import i18next from 'i18next';
import { Alert, ButtonGroup, Col, Row } from 'react-bootstrap';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useCatalogValueList } from '@/app/modules/configuration/catalogs/hooks/useCatalogValueList';
import { type CatalogModel } from '@/app/modules/configuration/catalogs/models/Catalog';

import { type FileDefinitionValue } from '../../../../core/files/types';
import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import ProgressBar from '../components/progress-bar';
import { useGetFilesDefinitionTaxpayerRequest } from '../hooks';

import { type ContactModel, type ProgressStepProps } from './types';

const validations = yup.object().shape({
  files: yup.array().of(
    yup.object().shape({
      file: yup.object().when('required', {
        is: (required: any) => required === true, // Si el archivo ES requerido
        then: (schema) => schema.required(i18next.t('common.validations.fieldRequired')),
        otherwise: (schema) => schema.notRequired()
      })
    })
  )
});

interface Props {
  onSubmit: (data: any) => void;
  definitions: Array<CatalogModel>;
  contactDefinition?: CatalogModel;
  data: Array<FileDefinitionValue>;
  readonly?: boolean;
}

export const Documents = ({ onSubmit, data, readonly, definitions, contactDefinition }: Props) => {
  const { t, i18n } = useTranslation();

  const [files, setFiles] = useState<Array<FileDefinitionValue>>([]);
  const [init, setInit] = useState<boolean>(false);
  const contacts = useTaxpayerRequestStore((s) => s.form.contacts);
  useEffect(() => {
    const newData = [] as Array<FileDefinitionValue>;

    definitions.forEach((x) => {
      const newValue = data?.find((y) => y.id === x.id);
      if (newValue) {
        newData.push({
          id: newValue.id,
          code: x.code,
          name: x.name,
          file: newValue.file,
          required: x?.additionalFields?.required as boolean,
          contentType: x?.additionalFields?.contentType as string
        });
      } else {
        newData.push({
          id: x.id,
          code: x.code,
          name: x.name,
          file: undefined,
          required: x?.additionalFields?.required as boolean,
          contentType: x?.additionalFields?.contentType as string
        });
      }
    });
    const newDataContacs = [] as Array<FileDefinitionValue>;

    if (contactDefinition) {
      contacts?.forEach((item: ContactModel, index) => {
        const newValue = data?.find((y) => y.id === item.rowId);
        const name = item.taxpayerName;
        const label = contactDefinition.name;
        if (newValue) {
          newDataContacs.push({
            id: item.rowId,
            name: `${label} ${name}`,
            file: newValue.file,
            required: contactDefinition.additionalFields?.required as boolean,
            contentType: contactDefinition.additionalFields?.contentType as string
          });
        } else {
          newDataContacs.push({
            id: item.rowId,
            name: `${label} ${name}`,
            file: undefined,
            required: contactDefinition.additionalFields?.required as boolean,
            contentType: contactDefinition.additionalFields?.contentType as string
          });
        }
      });
    }

    const allData = newData.concat(newDataContacs);
    setFiles(allData ?? []);
    setInit(true);
  }, []);

  const submit = (result: any) => {
    onSubmit(result);
  };

  if (!init) {
    return <></>;
  }

  if (files?.length === 0 || files === undefined || files === null) {
    return (
      <>
        <Row>
          <Col>
            <Alert variant="warning">{message(t, 'withoutDocuments')}</Alert>
          </Col>
        </Row>
      </>
    );
  }

  return (
    <>
      <HookFormResolver
        validations={validations}
        initialValues={{ files: files }}
        nameForm="formDocuments"
        onSubmit={submit}
      >
        {({ register, formState: { errors }, reset, setValue, control }: HookFormMethods) => {
          const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
            control,
            name: 'files'
          });

          return (
            <>
              <hr />

              {fields.map((obj: any, index: number) => {
                const value = obj as FileDefinitionValue;
                return (
                  <Row key={value.id}>
                    <Col>
                      <input {...register(`files[${index}].id`)} hidden defaultValue={value.id} />
                      <input
                        {...register(`files[${index}].required`)}
                        type="checkbox"
                        hidden
                        defaultChecked={value.required}
                      />
                      <File
                        label={value.name as string}
                        accept={value.contentType}
                        name={`files[${index}].file`}
                        defaultValue={value?.file}
                        readonly={readonly}
                        required={value.required}
                      />
                    </Col>
                  </Row>
                );
              })}
            </>
          );
        }}
      </HookFormResolver>
    </>
  );
};

export default (props: ProgressStepProps) => {
  const documents = useTaxpayerRequestStore((s) => s.form.documents);
  const addDocumentsData = useTaxpayerRequestStore((s) => s.addDocumentsData);
  const { data: definitions, loading, error } = useGetFilesDefinitionTaxpayerRequest();

  const {
    isLoading: loadingDefinition,
    data: contactFilesDefinitions,
    error: errorDefinition
  } = useCatalogValueList('contact-files-definition');

  const onSubmit = (data: { files: Array<FileDefinitionValue> }) => {
    addDocumentsData(data?.files);
    props.nextStep();
  };

  if (loading || error || loadingDefinition || errorDefinition) {
    return <></>;
  }
  const [contactDefinition] = (contactFilesDefinitions || []).filter(
    (x) => x.additionalFields.enable
  );
  return (
    <>
      <Documents
        contactDefinition={contactDefinition}
        definitions={definitions || []}
        data={documents ? documents : []}
        onSubmit={onSubmit}
      />
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <ButtonGroup>
          <Button
            form="formTextData"
            action={ButtonAction.previous}
            onClick={() => props.previousStep()}
          />
        </ButtonGroup>
        <ButtonGroup>
          <Button
            variant="primary"
            form="formDocuments"
            action={ButtonAction.next}
            type={'submit'}
          />
        </ButtonGroup>
      </BtnToolbar>
    </>
  );
};
